import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`API Error ${status}:`, data);
      
      throw new Error(data.detail || `API Error: ${status}`);
    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error:', error.request);
      throw new Error('Network error - please check your connection');
    } else {
      // Something else happened
      console.error('Request Error:', error.message);
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
);

// API Service methods
export const calculatorApi = {
  // Get pricing information
  async getPricing() {
    try {
      const response = await apiClient.get('/calculator/pricing');
      return response.data;
    } catch (error) {
      console.error('Error fetching pricing:', error);
      throw error;
    }
  },

  // Calculate estimate
  async calculateEstimate(windows) {
    try {
      const response = await apiClient.post('/calculator/estimate/calculate', {
        windows: windows
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating estimate:', error);
      throw error;
    }
  },

  // Save estimate
  async saveEstimate(customerName, customerEmail, windows, estimate) {
    try {
      const response = await apiClient.post('/calculator/estimate/save', {
        customer_name: customerName,
        customer_email: customerEmail,
        windows: windows,
        estimate: estimate
      });
      return response.data;
    } catch (error) {
      console.error('Error saving estimate:', error);
      throw error;
    }
  },

  // Get estimate by ID
  async getEstimate(estimateId) {
    try {
      const response = await apiClient.get(`/calculator/estimate/${estimateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching estimate:', error);
      throw error;
    }
  },

  // Email estimate
  async emailEstimate(customerName, customerEmail, customerPostcode, windows, estimate) {
    try {
      const response = await apiClient.post('/calculator/estimate/email', {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_postcode: customerPostcode,
        windows: windows,
        estimate: estimate
      });
      return response.data;
    } catch (error) {
      console.error('Error emailing estimate:', error);
      throw error;
    }
  },

  // Request home visit
  async requestHomeVisit(visitRequest) {
    try {
      const response = await apiClient.post('/calculator/home-visit/request', visitRequest);
      return response.data;
    } catch (error) {
      console.error('Error requesting home visit:', error);
      throw error;
    }
  },

  // Create pipeline entry
  async createPipelineEntry(pipelineData) {
    try {
      const response = await apiClient.post('/calculator/pipeline/create', pipelineData);
      return response.data;
    } catch (error) {
      console.error('Error creating pipeline entry:', error);
      throw error;
    }
  },

  // Get pipeline entries
  async getPipelineEntries(stage = 'all', limit = 100) {
    try {
      const response = await apiClient.get(`/calculator/pipeline/entries?stage=${stage}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pipeline entries:', error);
      throw error;
    }
  },

  // Update pipeline stage
  async updatePipelineStage(pipelineId, stage, notes = null) {
    try {
      const response = await apiClient.patch(`/calculator/pipeline/${pipelineId}/stage`, {
        stage: stage,
        notes: notes
      });
      return response.data;
    } catch (error) {
      console.error('Error updating pipeline stage:', error);
      throw error;
    }
  }
};

// Test API connection
export const testApiConnection = async () => {
  try {
    const response = await apiClient.get('/');
    console.log('API Connection Test:', response.data);
    return true;
  } catch (error) {
    console.error('API Connection Failed:', error);
    return false;
  }
};

export default apiClient;